// FirewallCompliance.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Search, ShieldAlert,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ChevronDown, CheckCircle2, XCircle,
    AlertTriangle, AlertCircle, Info, Tag, Code2,
    BookOpen, Wrench, FolderOpen,
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../../components/ItemsPerPageDropdown ";

// ── Types ─────────────────────────────────────────────────────────────────────
interface ComplianceItem {
    id: number;
    name: string;
    status: string;
    evidence: string;
    mitigation_status: string;
    remarks: string;
    check_name: string;
    api_endpoint: string;
    category: string;
}

interface ComplianceSummary {
    not_applicable: any;
    info: number;
    score: number;
    total: number;
    errors: number;
    compliant: number;
    non_compliant: number;
}

// ── Status Config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, {
    icon: React.ElementType;
    bg: string;
    border: string;
    text: string;
    badge: string;
    dot: string;
    rowHover: string;
}> = {
    "Compliant": {
        icon: CheckCircle2,
        bg: "bg-green-500/5",
        border: "border-green-500/20",
        text: "text-green-400",
        badge: "bg-green-500/10 border-green-500/20 text-green-400",
        dot: "bg-green-400",
        rowHover: "hover:border-green-500/30",
    },
    "Non-Compliant": {
        icon: XCircle,
        bg: "bg-red-500/5",
        border: "border-red-500/20",
        text: "text-red-400",
        badge: "bg-red-500/10 border-red-500/20 text-red-400",
        dot: "bg-red-400",
        rowHover: "hover:border-red-500/30",
    },
    "Error": {
        icon: AlertCircle,
        bg: "bg-yellow-500/5",
        border: "border-yellow-500/20",
        text: "text-yellow-400",
        badge: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400",
        dot: "bg-yellow-400",
        rowHover: "hover:border-yellow-500/30",
    },
    "Info": {
        icon: Info,
        bg: "bg-blue-500/5",
        border: "border-blue-500/20",
        text: "text-blue-400",
        badge: "bg-blue-500/10 border-blue-500/20 text-blue-400",
        dot: "bg-blue-400",
        rowHover: "hover:border-blue-500/30",
    },
    "Not Applicable": {
        icon: AlertTriangle,
        bg: "bg-cyan-500/5",
        border: "border-cyan-500/20",
        text: "text-cyan-400",
        badge: "bg-cyan-500/10 border-cyan-500/20 text-cyan-400",
        dot: "bg-cyan-400",
        rowHover: "hover:border-cyan-500/30",
    },
};

const getStatusCfg = (status: string) =>
    STATUS_CONFIG[status] ?? {
        icon: AlertTriangle,
        bg: "bg-gray-500/5",
        border: "border-gray-500/20",
        text: "text-gray-400",
        badge: "bg-gray-500/10 border-gray-500/20 text-gray-400",
        dot: "bg-gray-400",
        rowHover: "hover:border-gray-500/30",
    };

const CATEGORY_LABELS: Record<string, string> = {
    system_configuration: "System Configuration",
    security_configuration: "Security Configuration",
    firewall_policies: "Firewall Policies",
    logging_and_reporting: "Logging & Reporting",
    security_profiles: "Security Profiles",
    high_availability: "High Availability",
    vpn_configuration: "VPN Configuration",
    backup_and_notifications: "Backup & Notifications",
};

// ── Summary Cards ─────────────────────────────────────────────────────────────
const SummaryCards = ({ summary }: { summary: ComplianceSummary }) => {
    const scoreColor =
        summary.score >= 75 ? "text-green-400" :
            summary.score >= 50 ? "text-yellow-400" :
                "text-red-400";

    const cards = [
        {
            label: "Compliant",
            value: summary.compliant,
            icon: CheckCircle2,
            bg: "bg-green-500/5",
            border: "border-green-500/20",
            text: "text-green-400",
            iconBg: "bg-green-500/10 border-green-500/20",
        },
        {
            label: "Non-Compliant",
            value: summary.non_compliant,
            icon: XCircle,
            bg: "bg-red-500/5",
            border: "border-red-500/20",
            text: "text-red-400",
            iconBg: "bg-red-500/10 border-red-500/20",
        },
        {
            label: "Errors",
            value: summary.errors,
            icon: AlertCircle,
            bg: "bg-yellow-500/5",
            border: "border-yellow-500/20",
            text: "text-yellow-400",
            iconBg: "bg-yellow-500/10 border-yellow-500/20",
        },
        {
            label: "Info",
            value: summary.info,
            icon: Info,
            bg: "bg-blue-500/5",
            border: "border-blue-500/20",
            text: "text-blue-400",
            iconBg: "bg-blue-500/10 border-blue-500/20",
        },
        {
            label: "Not Applicable",
            value: summary.not_applicable,
            icon: AlertTriangle,
            bg: "bg-cyan-700/20",
            border: "border-cyan-700/30",
            text: "text-cyan-300",
            iconBg: "bg-cyan-700/30 border-cyan-700/30",
        },
        {
            label: "Total Checks",
            value: summary.total,
            icon: ShieldAlert,
            bg: "bg-gray-700/20",
            border: "border-gray-600/30",
            text: "text-gray-300",
            iconBg: "bg-gray-700/30 border-gray-600/30",
        },

    ];

    return (
        <div className="flex items-stretch gap-3 mb-4">
            {/* Score Ring Card */}
            <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-gray-800/50 border border-gray-700/60 rounded-xl shrink-0">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border bg-gray-700/30 border-gray-600/30`}>
                    <ShieldAlert size={13} className="text-gray-300" />
                </div>
                <div>
                    <p className={`text-[20px] font-black leading-none ${scoreColor}`}>{summary.score}%</p>
                    <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mt-0.5">Score</p>
                </div>
            </div>

            {/* Stat Cards */}
            {cards.map(({ label, value, icon: Icon, bg, border, text, iconBg }) => (
                <div
                    key={label}
                    className={`flex-1 flex items-center gap-2.5 px-3 py-3 rounded-xl border ${bg} ${border}`}
                >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border ${iconBg}`}>
                        <Icon size={13} className={text} />
                    </div>
                    <div>
                        <p className={`text-[20px] font-black leading-none ${text}`}>{value}</p>
                        <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mt-0.5 whitespace-nowrap">{label}</p>
                    </div>
                </div>
            ))}
        </div>
    );
};

// ── Expandable Row ────────────────────────────────────────────────────────────
const ComplianceRow = ({ item }: { item: ComplianceItem }) => {
    const [open, setOpen] = useState(false);
    const cfg = getStatusCfg(item.status);
    const Icon = cfg.icon;
    const categoryLabel = CATEGORY_LABELS[item.category] ?? item.category;

    return (
        <div className={`border rounded-xl overflow-hidden transition-all duration-200 ${open ? `${cfg.border} ${cfg.bg}` : `border-gray-700/60 bg-gray-800 ${cfg.rowHover}`
            }`}>

            {/* Row Header */}
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left group transition-all"
            >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border ${cfg.bg} ${cfg.border}`}>
                    <Icon size={13} className={cfg.text} />
                </div>

                <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold text-gray-100 truncate leading-tight">{item.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[9px] text-gray-200 font-mono">{item.check_name}</span>
                        <span className="text-gray-700">·</span>
                        <span className="text-[9px] text-gray-200">{categoryLabel}</span>
                    </div>
                </div>

                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black border uppercase shrink-0 ${cfg.badge}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                    {item.status}
                </span>

                {item.mitigation_status !== "-" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border uppercase shrink-0 bg-orange-500/10 border-orange-500/20 text-orange-400">
                        <Wrench size={8} />
                        {item.mitigation_status}
                    </span>
                )}

                <div className={`transition-transform duration-200 shrink-0 ml-1 ${open ? "rotate-180" : ""}`}>
                    <ChevronDown size={14} className="text-gray-200 group-hover:text-gray-300 transition-colors" />
                </div>
            </button>

            {/* Expanded Details */}
            {open && (
                <div className="border-t border-gray-700/50 px-4 pb-4 pt-3 space-y-3 animate-fade-in">
                    <div>
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <BookOpen size={10} className="text-gray-200" />
                            <p className="text-[9px] font-black text-gray-200 uppercase tracking-widest">Evidence</p>
                        </div>
                        <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg px-3 py-2.5">
                            <pre className="text-[10px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                                {item.evidence}
                            </pre>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Wrench size={10} className="text-gray-200" />
                                <p className="text-[9px] font-black text-gray-200 uppercase tracking-widest">Remarks</p>
                            </div>
                            <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg px-3 py-2">
                                <p className="text-[10px] text-gray-300">{item.remarks || "None"}</p>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <AlertTriangle size={10} className="text-gray-200" />
                                <p className="text-[9px] font-black text-gray-200 uppercase tracking-widest">Mitigation</p>
                            </div>
                            <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg px-3 py-2">
                                <p className={`text-[10px] font-bold ${item.mitigation_status === "-" ? "text-gray-200" : "text-orange-400"
                                    }`}>
                                    {item.mitigation_status}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 pt-1 flex-wrap">
                        <div className="flex items-center gap-1.5">
                            <Tag size={9} className="text-gray-200" />
                            <span className="text-[9px] text-gray-200 font-mono">{item.check_name}</span>
                        </div>
                        <span className="text-gray-700">·</span>
                        <div className="flex items-center gap-1.5">
                            <Code2 size={9} className="text-gray-200" />
                            <span className="text-[9px] text-gray-200 font-mono">{item.api_endpoint}</span>
                        </div>
                        <span className="text-gray-700">·</span>
                        <div className="flex items-center gap-1.5">
                            <FolderOpen size={9} className="text-gray-200" />
                            <span className="text-[9px] text-gray-200">{categoryLabel}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallCompliance({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [items, setItems] = useState<ComplianceItem[]>([]);
    const [summary, setSummary] = useState<ComplianceSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => { setCurrentPage(1); }, [statusFilter]);

    // ── Fetch summary once on mount ───────────────────────────────────────────
    useEffect(() => {
        if (!asset_id) return;
        setSummaryLoading(true);
        baseapi.get("/firewall_asset_details", {
            params: { query: JSON.stringify({ asset_id, client_id }), limit: 1 }
        })
            .then(res => {
                if (res.data.status && res.data.data?.[0]?.compliance_summary) {
                    try {
                        const raw = res.data.data[0].compliance_summary;
                        setSummary(typeof raw === "string" ? JSON.parse(raw) : raw);
                    } catch { /* silent */ }
                }
            })
            .catch(() => { /* silent — cards just won't show */ })
            .finally(() => setSummaryLoading(false));
    }, [asset_id]);

    // ── Fetch compliance list ─────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) queryObj.$or = [
                { name: { $ilike: `%${debouncedSearch}%` } },
                { check_name: { $ilike: `%${debouncedSearch}%` } },
                { category: { $ilike: `%${debouncedSearch}%` } },
            ];
            if (statusFilter !== "all") queryObj["status"] = statusFilter;

            const res = await baseapi.get("/firewall_compliance", {
                params: {
                    query: JSON.stringify(queryObj),
                    skip: (currentPage - 1) * limit,
                    limit,
                }
            });
            if (res.data.status) {
                setItems(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load compliance data");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, debouncedSearch, statusFilter, currentPage, limit]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);
    const STATUS_FILTERS = ["all", "Compliant", "Non-Compliant", "Error", "Info", "Not Applicable"];

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Summary Cards */}
            {summaryLoading ? (
                <div className="flex items-center gap-2 mb-4 px-1 text-gray-600">
                    <Loader2 size={12} className="animate-spin" />
                    <span className="text-[10px] uppercase tracking-widest font-bold">Loading summary...</span>
                </div>
            ) : summary && (
                <SummaryCards summary={summary} />
            )}

            {/* Action Bar */}
            <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="bg-rose-600/20 p-1.5 rounded-lg border border-rose-500/30">
                        <ShieldAlert size={16} className="text-rose-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Compliance Checks</h1>
                    <span className="text-[10px] font-bold text-gray-200 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded">
                        {totalRecords} checks
                    </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {/* Status Filter Pills */}
                    <div className="flex items-center gap-1 bg-gray-800/60 border border-gray-700 rounded-lg p-0.5">
                        {STATUS_FILTERS.map(s => {
                            const isActive = statusFilter === s;
                            const cfg = s === "all" ? null : getStatusCfg(s);
                            return (
                                <button
                                    key={s}
                                    onClick={() => setStatusFilter(s)}
                                    className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${isActive
                                        ? s === "all"
                                            ? "bg-gray-600 text-white"
                                            : `${cfg!.bg} border ${cfg!.border} ${cfg!.text}`
                                        : "text-gray-200 hover:text-gray-300"
                                        }`}
                                >
                                    {s === "all" ? "All" : s}
                                </button>
                            );
                        })}
                    </div>

                    {/* Search */}
                    <div className="relative group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-rose-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search checks..."
                            className="w-48 bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-rose-500/50 transition-all"
                        />
                    </div>

                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-rose-400"} />
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="space-y-2">
                {loading ? (
                    <div className="flex items-center justify-center gap-2 py-16 text-gray-200">
                        <Loader2 className="w-4 h-4 animate-spin text-rose-400" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Loading compliance checks...</span>
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                        <ShieldAlert size={24} className="text-gray-400" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">No compliance checks found</span>
                    </div>
                ) : (
                    items.map(item => <ComplianceRow key={item.id} item={item} />)
                )}
            </div>

            {/* Pagination Footer */}
            {!loading && items.length > 0 && (
                <div className="flex items-center justify-between mt-4 px-1">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-200 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-600">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {(debouncedSearch || statusFilter !== "all") && (
                            <button
                                onClick={() => { setSearchTerm(""); setStatusFilter("all"); }}
                                className="text-[9px] text-rose-400 hover:text-rose-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-400 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
